class SizePreferencePLP {
  constructor() {
    this.hasInitialized = false;
    this.init();
  }

  init() {
    // Only run once on first DOM content loaded
    if (this.hasInitialized) return;
    
    // Check if we're on a collection page
    if (!this.isCollectionPage()) return;
    
    this.hasInitialized = true;
    
    // Always set up navigation interception first
    this.setupPageExitListener();
    
    // Then check if preferences have already been applied in this session
    if (this.hasPreferencesBeenApplied()) {
      return;
    }
    
    this.processStoredPreferences();
  }

  isCollectionPage() {
    // Check if current page is a collection page
    return window.location.pathname.includes('/collections/') && 
           !window.location.pathname.includes('/products/');
  }

  hasPreferencesBeenApplied() {
    // Check if preferences have already been applied in this session
    const sessionKey = 'sizePreferencesApplied';
    
    try {
      const applied = sessionStorage.getItem(sessionKey);
      return applied === 'true';
    } catch (error) {
      console.warn('Failed to check session storage:', error);
    }
    
    return false;
  }

  clearSessionStorage() {
    // Clear the session storage when leaving collection pages
    const sessionKey = 'sizePreferencesApplied';
    try {
      sessionStorage.removeItem(sessionKey);
      
      // If removeItem didn't work, try setting to empty string then removing
      if (sessionStorage.getItem(sessionKey) !== null) {
        sessionStorage.setItem(sessionKey, '');
        sessionStorage.removeItem(sessionKey);
      }
    } catch (error) {
      console.warn('Failed to clear session storage:', error);
    }
  }

  setupPageExitListener() {
    // Check if user came from a non-collection page (indicating they're entering collection for first time)
    const referrer = document.referrer;
    const cameFromCollection = referrer && this.isCollectionPageFromUrl(referrer);
    
    // If user didn't come from a collection page, clear any existing session storage
    // This ensures fresh application when entering collections from other pages
    if (!cameFromCollection) {
      this.clearSessionStorage();
    }
    
    // Set up navigation interception to clear session storage when leaving collection
    this.setupNavigationInterception();
  }

  setupNavigationInterception() {
    // Intercept all link clicks
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a');
      if (link && link.href) {
        this.checkNavigationDestination(link.href);
      }
    });

    // Intercept programmatic navigation (history API)
    this.interceptHistoryAPI();
  }

  checkNavigationDestination(url) {
    try {
      const urlObj = new URL(url);
      const isLeavingCollection = this.isLeavingCollectionPage(urlObj);
      
      if (isLeavingCollection) {
        this.clearSessionStorage();
      }
    } catch (error) {
      console.warn('Failed to parse navigation URL:', error);
    }
  }

  isLeavingCollectionPage(urlObj) {
    // Check if the destination URL is not a collection page
    const isDestinationCollection = urlObj.pathname.includes('/collections/') && 
                                   !urlObj.pathname.includes('/products/');
    
    // If we're currently on a collection page and destination is not a collection page
    return this.isCollectionPage() && !isDestinationCollection;
  }

  interceptHistoryAPI() {
    // Store original methods
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    // Override pushState
    history.pushState = (...args) => {
      const [state, title, url] = args;
      if (url) {
        this.checkNavigationDestination(url);
      }
      return originalPushState.apply(history, args);
    };

    // Override replaceState
    history.replaceState = (...args) => {
      const [state, title, url] = args;
      if (url) {
        this.checkNavigationDestination(url);
      }
      return originalReplaceState.apply(history, args);
    };

    // Listen for popstate (back/forward navigation)
    window.addEventListener('popstate', () => {
      // Check current URL after navigation
      setTimeout(() => {
        if (!this.isCollectionPage()) {
          this.clearSessionStorage();
        }
      }, 100);
    });
  }

  isCollectionPageFromUrl(url) {
    // Check if a given URL is a collection page
    try {
      const urlObj = new URL(url);
      return urlObj.pathname.includes('/collections/') && 
             !urlObj.pathname.includes('/products/');
    } catch (error) {
      return false;
    }
  }

  markPreferencesAsApplied() {
    // Mark that preferences have been applied
    const sessionKey = 'sizePreferencesApplied';
    
    try {
      sessionStorage.setItem(sessionKey, 'true');
    } catch (error) {
      console.warn('Failed to save to session storage:', error);
    }
  }

  processStoredPreferences() {
    const storedPreferences = this.getStoredPreferences();
      
    // Get available facets for this collection
    const availableFacets = this.getAvailableFacets();
    if (!availableFacets || availableFacets.length === 0) {
      // Even without size facets, ensure in-stock filter is applied
      this.ensureInStockFilter({});
      // Mark as applied even if no size facets
      this.markPreferencesAsApplied();
      return;
    }

    // Filter preferences based on available facets
    const filteredPreferences = storedPreferences ? 
    this.filterPreferencesByAvailableFacets(storedPreferences.selectedSizes, availableFacets) : {};

    // Apply preferences (or just in-stock filter if no preferences)
    this.selectFacetInputs(filteredPreferences);
    
    // Mark preferences as applied after processing
    this.markPreferencesAsApplied();
  }

  getAvailableFacets() {
    // Get all facet summary labels to see what size options are available
    const facetLabels = document.querySelectorAll('.facets__summary-label');
    const availableFacets = [];

    facetLabels.forEach(label => {
      // Get the text content and clean it up
      let labelText = label.textContent.trim();
      
      // Remove the count information like " (0)" that appears in hidden spans
      labelText = labelText.replace(/\s*\(\d+\)\s*$/, '').trim();
            
      // Only check for Top Size and Bottom Size (bottom size contains inseam info)
      const facetMapping = {
        'Top Size': 'top_sizes',
        'Bottom Size': 'bottom_sizes',
        'Shoe Size': 'shoe_sizes',
      };

      // Check for exact match
      if (facetMapping[labelText]) {
        availableFacets.push({
          label: labelText,
          preferenceKey: facetMapping[labelText],
          originalLabel: labelText
        });
      }
    });

    return availableFacets;
  }

  filterPreferencesByAvailableFacets(selectedSizes, availableFacets) {
    const filteredPreferences = {};
    
    // Create a map of available preference keys
    const availableKeys = availableFacets.map(facet => facet.preferenceKey);
    
    // Only include preferences that have corresponding facets
    Object.entries(selectedSizes).forEach(([key, values]) => {
      if (availableKeys.includes(key) && values && values.length > 0) {
        filteredPreferences[key] = values;
      }
    });

    return filteredPreferences;
  }

  getStoredPreferences() {
    try {
      const stored = localStorage.getItem('sizePreferences');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.warn('Failed to parse stored preferences:', error);
      return null;
    }
  }

  // New method: Find and select facet inputs
  selectFacetInputs(preferences) {
    const facetInputs = this.discoverFacetInputs();
    const mappedPreferences = this.mapPreferencesToFacets(preferences, facetInputs);
    
    // Clear existing selections
    this.clearFacetSelections();
    
    // Always ensure in-stock availability is selected
    this.ensureInStockFilter(facetInputs);
    
    // Select matching inputs
    this.applyFacetSelections(mappedPreferences);
    
    // Trigger facet form submission
    this.triggerFacetFormSubmission();
  }
  
  // New method: Discover available facet inputs
  discoverFacetInputs() {
    const inputs = document.querySelectorAll('#FacetFiltersForm input[type="checkbox"]');
    const facetGroups = {};
    
    inputs.forEach(input => {
      const name = input.name; // e.g., "filter.v.option.top size"
      const value = input.value; // e.g., "Medium", "Small"
      const label = this.getInputLabel(input);
      
      if (!facetGroups[name]) {
        facetGroups[name] = [];
      }
      facetGroups[name].push({
        input: input,
        value: value,
        label: label,
        labelElement: input.closest('.facets__label')
      });
    });
    
    return facetGroups;
  }
  
  // New method: Get the human-readable label for an input
  getInputLabel(input) {
    const labelElement = input.closest('.facets__label');
    if (labelElement) {
      const textLabel = labelElement.querySelector('.facet-checkbox__text-label');
      return textLabel ? textLabel.textContent.trim() : input.value;
    }
    return input.value;
  }
  
  // New method: Ensure in-stock availability filter is always selected
  ensureInStockFilter(facetInputs) {
    // Look for availability filter inputs
    const availabilityInputs = this.findAvailabilityInputs();
    
    if (availabilityInputs.length > 0) {
      // Find the "in stock" or "available" option
      const inStockInput = this.findInStockInput(availabilityInputs);
      
      if (inStockInput) {
        inStockInput.checked = true;
        this.updateLabelState(inStockInput, true);
      }
    }
  }
  
  // New method: Find availability filter inputs
  findAvailabilityInputs() {
    // Look for common availability filter names
    const availabilitySelectors = [
      'input[name*="availability"]',
      'input[name*="stock"]',
      'input[name*="in stock"]',
      'input[name*="available"]'
    ];
    
    const inputs = [];
    availabilitySelectors.forEach(selector => {
      const found = document.querySelectorAll(`#FacetFiltersForm ${selector}`);
      found.forEach(input => inputs.push(input));
    });
    
    return inputs;
  }
  
  // New method: Find the in-stock input from availability options
  findInStockInput(availabilityInputs) {
    // Look for inputs that represent "in stock" or "available" status
    const inStockKeywords = ['in stock', 'available', '1', 'true', 'yes'];
    
    for (const input of availabilityInputs) {
      const value = input.value.toLowerCase();
      const label = this.getInputLabel(input).toLowerCase();
      
      // Check if this input represents in-stock status
      const isInStock = inStockKeywords.some(keyword => 
        value.includes(keyword) || label.includes(keyword)
      );
      
      if (isInStock) {
        return input;
      }
    }
    
    // If no specific in-stock input found, return the first availability input
    return availabilityInputs[0] || null;
  }
  
  // New method: Map preferences to available facets
  mapPreferencesToFacets(preferences, facetInputs) {
    const mapping = {
      'top_sizes': 'filter.v.option.top size',
      'bottom_sizes': 'filter.v.option.bottom size', 
      'shoe_sizes': 'filter.v.option.shoe size'
    };
    
    const selections = {};
    
    Object.entries(preferences).forEach(([prefKey, prefValues]) => {
      const facetName = mapping[prefKey];
      if (facetName && facetInputs[facetName]) {
        selections[facetName] = this.findMatchingFacetValues(prefValues, facetInputs[facetName]);
      }
    });
    
    return selections;
  }
  
  // New method: Find matching facet values for preferences
  findMatchingFacetValues(prefValues, facetInputs) {
    const matches = [];
    
    prefValues.forEach(prefValue => {
      // Map preference value to facet value format
      const mappedValue = this.mapPreferenceToFacetValue(prefValue);
      
      // Find matching facet input
      const matchingInput = facetInputs.find(facet => 
        facet.value.toLowerCase() === mappedValue.toLowerCase() ||
        facet.label.toLowerCase() === mappedValue.toLowerCase()
      );
      
      if (matchingInput) {
        matches.push(matchingInput);
      }
    });
    
    return matches;
  }
  
  // New method: Map preference values to facet values
  mapPreferenceToFacetValue(prefValue) {
    // Handle different size formats
    const sizeMap = {
      'S': 'Small',
      'M': 'Medium', 
      'L': 'Large',
      'XL': 'XL',
      '2XL': 'XXL',
      '3XL': 'XXXL'
    };
    
    // Check if it's a bottom size (waistxinseam format)
    if (prefValue.includes('x')) {
      return prefValue; // Keep as-is for bottom sizes
    }
    
    // Map top sizes
    return sizeMap[prefValue] || prefValue;
  }
  
  // New method: Clear all facet selections (but preserve in-stock filter)
  clearFacetSelections() {
    const allInputs = document.querySelectorAll('#FacetFiltersForm input[type="checkbox"]:checked');
    allInputs.forEach(input => {
      // Don't clear availability filters - we want to keep in-stock selected
      if (!this.isAvailabilityInput(input)) {
        input.checked = false;
        this.updateLabelState(input, false);
      }
    });
  }
  
  // New method: Check if an input is an availability filter
  isAvailabilityInput(input) {
    const name = input.name.toLowerCase();
    return name.includes('availability') || name.includes('stock') || name.includes('available');
  }
  
  // New method: Apply facet selections
  applyFacetSelections(selections) {
    Object.entries(selections).forEach(([facetName, facetInputs]) => {
      facetInputs.forEach(facetInput => {
        facetInput.input.checked = true;
        this.updateLabelState(facetInput.input, true);
      });
    });
  }
  
  // New method: Update label visual state
  updateLabelState(input, isActive) {
    const label = input.closest('.facets__label');
    if (label) {
      if (isActive) {
        label.classList.add('active');
      } else {
        label.classList.remove('active');
      }
    }
  }
  
  // New method: Trigger facet form submission
  triggerFacetFormSubmission() {
    // Use the existing FacetFiltersForm system
    const facetForm = document.querySelector('facet-filters-form');
    if (facetForm) {
      // Create a synthetic input event to trigger the facet form's debounced submit
      const event = new Event('input', { bubbles: true });
      facetForm.querySelector('form').dispatchEvent(event);
    }
  }

  // New method: Integrate with facet form system
  integrateWithFacetForm() {
    // Get the facet form instance
    const facetForm = document.querySelector('facet-filters-form');
    if (!facetForm) return;
    
    // Store reference to facet form methods
    this.facetForm = facetForm;
    
    // Listen for facet changes to sync with preferences
    this.setupFacetChangeListener();
    
    // Listen for preference changes
    this.setupPreferenceChangeListener();
    
    // Ensure in-stock filter is always applied on page load
    this.ensureInStockFilterOnLoad();
  }
  
  // New method: Ensure in-stock filter is applied on page load
  ensureInStockFilterOnLoad() {
    // Wait for facets to be fully loaded
    setTimeout(() => {
      const facetInputs = this.discoverFacetInputs();
      this.ensureInStockFilter(facetInputs);
    }, 100);
  }
  
  // New method: Setup facet change listener
  setupFacetChangeListener() {
    const facetForm = document.querySelector('#FacetFiltersForm');
    if (facetForm) {
      facetForm.addEventListener('change', (event) => {
        if (event.target.type === 'checkbox') {
          this.onFacetChange(event);
        }
      });
    }
  }
  
  // New method: Handle facet changes
  onFacetChange(event) {
    const input = event.target;
    const isChecked = input.checked;
    
    // Update visual state
    this.updateLabelState(input, isChecked);
    
    // If availability filter is unchecked, re-check it
    if (this.isAvailabilityInput(input) && !isChecked) {
      input.checked = true;
      this.updateLabelState(input, true);
      return;
    }
    
    // Optionally sync with preferences
    this.syncFacetToPreferences(input, isChecked);
  }
  
  // New method: Setup preference change listener
  setupPreferenceChangeListener() {
    window.addEventListener('sizePreferencesChanged', (event) => {
      this.applyPreferencesToFacets(event.detail.preferences);
    });
  }
  
  // New method: Apply preferences to facets
  applyPreferencesToFacets(preferences) {
    this.selectFacetInputs(preferences);
  }
}

// Initialize on DOM content loaded
document.addEventListener('DOMContentLoaded', () => {
  new SizePreferencePLP();
});